import * as React from "react";
import { renderToString } from "react-dom/server";
import type { History } from "history";
import type { SyntheticEvent } from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "react-bootstrap";
import { translate, IntlProviderWrapper } from "../IntlProviderWrapper";
import LinePlanningModal from "./LinePlanningModal";
import LineInsightListModal from "./LineInsightListModal";
import LineMaterialsModal from "./LineMaterialsModal";
import GantchartStandalone from "./GantchartStandalone";
import PlanShiftTableProduction from "./PlanShiftTableProduction";
import OrderTableProduction, { WORK_ORDER_TABLE_COLUMNS } from "./OrderTableProduction";
import Loader from "../Loader";
import { createPortal } from "react-dom";
import { niceDateTime, SHIFT_CONSTS, classNames } from "../../lib/Util";
import type { Dispatch } from "redux";
import { connect } from "react-redux";
import { PERMISSION_NAMES } from "../../lib/Auth";
import { getGanttDataSimpleAsync } from "../../lib/GanttData";
import { getBackend as getBackend2 } from "../../lib/backend/Backend2";
import * as t from "../../lib/backend/manufacturing2.generated.types";
import { getEmptyWeekShiftGrid } from "../../lib/Insights";
import * as rt from "../../lib/SimulationReportModels";
import * as rrt from "../../lib/backend/reports.generated.types";
import type { MaterialForecastType } from "../../lib/StockForecast";
import {
    MATERIAL_FORECAST_SUBTYPES,
    MATERIAL_FORECAST_ONLY_ACCOUNTED,
    MATERIAL_FORECAST_LOCATIONS,
} from "../../lib/StockForecast";
import { dateFromWeekAndShift, TIME_RANGES, shiftNumber, createHtmlAndDownload, addHours } from "../../lib/Util";
import {
    LINE_GROUP_TAGS,
    INSIGHT_TYPES,
    PLANT_TAGS,
    LINE_GROUP_TAGS_ACCESS,
    ORDER_TAGS,
    PLANT_TAGS_ACCESS,
} from "../../lib/ManufacturingTags.generated";
import { loadRealization } from "../../lib/WeeklyRealization";
import * as BusinessLogic from "../../lib/BusinessLogic";
import local_settings from "../../lib/LocalSettings";
import Authorization from "../Authorization";
import LineStatus from "./PlanningTable2/GanttChart/LineStatus";
import { ReportLogic } from "./PlanningTable2/reducers/BusinessLogic";
import { SET_TRANSLATIONS_MATERIALS } from "../redux/reducers/translationsMaterialsReducer";
import type { GanttChartSourceTypes } from "./PlanningTable2/reducers/properties";
import MultiSelect from "../MultiSelect";
import { FIELD_MICROPLAN_ORDER_TYPES } from "../../lib/LocalSettings";
import type { ReduxState, ActionTypes } from "../redux/reducers";

import type { InsightWeekShiftGrid } from "../../lib/Insights";
import type { WeeklyReport } from "../../lib/Models";
import type { ILabelValuePair } from "../../lib/Util";

export const MICROPLAN_EXPORT_TYPE = {
    all: "all",
    shifts: "shifts",
    orders: "orders",
};

const PRINT_SETTINGS_KEY = "microplan-print-settings";

function eventsIntoWeekShiftsGrid(events: t.IEventDataEx[]): InsightWeekShiftGrid {
    const events_grid = getEmptyWeekShiftGrid();
    for (const event of events) {
        const event_time = event.ts;
        const shift_data = shiftNumber(new Date(event_time));
        events_grid["" + (Math.floor(shift_data.shift / 3) + 1)]["" + ((shift_data.shift % 3) + 1)].push(event);
    }
    return events_grid;
}

export function exportMicroplanHTML(html_string: string, print_timestamp: boolean): void {
    const html = `
        <html>
            <head>
                <title>${translate("Manufacturing.LinesView.microplan", "Microplan")}</title>
                <meta charSet="utf-8" />
                <style>
                    body {
                        font-family: "Arial", "Helvetica", "sans-serif";
                    }

                    body, table, p, div {
                        font-size: 12px;
                    }

                    h5 {
                        margin-bottom: 1em;
                        font-size: 18px;
                    }

                    h6 {
                        margin-top: 1em;
                        margin-bottom: 1em;
                        font-size: 14px;
                    }

                    h6 b::after {
                        content: ": ";
                    }

                    h6 i {
                        font-weight: 400;
                    }

                    table {
                        border-collapse: collapse;
                        border: 1px solid black;
                        width: 100%;
                    }

                    th, td {
                        border: 1px solid lightgray;
                        border-bottom: 0px solid lightgray;
                        padding: 13px;
                        text-align: center;
                    }

                    button {
                        display: none;
                    }

                    .row > .col-lg-6 + .col-lg-6 {
                        margin-top: 32px;
                    }

                    .font-italic {
                        font-style: italic;
                    }

                    .pane {
                        break-after: page;
                    }

                    .order-table .material img {
                        display: none;
                    }

                    .order-table .work-order-actions {
                        display: none !important;
                    }

                    .order-table .line-order-shifts {
                        padding: 0;
                    }

                    .order-table-shifts {
                        border-width: 0;
                    }

                    .order-table-shifts tr:first-child td {
                        border-top-width: 0;
                    }

                    .order-table-shifts td:first-child {
                        border-left-width: 0;
                    }

                    .order-table-shifts td:nth-last-child(2) {
                        border-right-width: 0;
                    }

                    .shift-table thead tr:last-child {
                        display: none;
                    }

                    .shift-table {
                        border: 0;
                        border-collapse: separate;
                    }

                    .shift-table thead th {
                        height: auto;
                        padding: 6px;
                        border-width: 0;
                        font-size: 14px;
                        font-weight: 400;
                        text-align: center;
                    }

                    .shift-table thead tr:nth-child(2) th {
                        font-size: 11px;
                    }

                    .shift-table tbody td {
                        height: 37px;
                        padding: 0;
                        font-weight: 400;
                        text-align: center;
                        border: 1px solid rgb(203, 210, 217);
                    }

                    .shift-table tbody td:first-child {
                        padding: 6px;
                        font-size: 14px;
                        border-width: 0;
                    }

                    .shift-table tbody td > div {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                    }

                    .shift-table tbody td > div > span {
                        display: flex;
                        flex-direction: column;
                        flex: 1;
                        justify-content: center;
                        line-height: 1;
                        border-left-style: solid;
                        border-left-width: 7px;
                    }

                    .shift-table tbody td > div > span + span {
                        margin-top: 1px;
                    }

                    .shift-table-days {
                        font-size: 14px !important;
                    }

                    .shift-color-done {
                        border-color: rgb(154, 165, 177);
                    }

                    .shift-color0 {
                        border-color: rgb(71, 163, 243);
                    }

                    .shift-color-no-work {
                        border-color: rgb(71, 163, 243);
                    }

                    .faded-cell .shift-color0 {
                        border-color: rgba(71, 163, 243, 0.4);
                    }

                    .shift-color1 {
                        border-color: rgb(94, 138, 238);
                    }

                    .faded-cell .shift-color1 {
                        border-color: rgba(94, 138, 238, 0.4);
                    }

                    .shift-color2 {
                        border-color: rgb(84, 209, 219);
                    }

                    .faded-cell .shift-color2 {
                        border-color: rgba(84, 209, 219, 0.4);
                    }

                    .selected-cell {
                        border: 2px solid rgb(248, 106, 106) !important;
                    }

                    .selected-span {
                        border: gray;
                        border-style: solid;
                        padding: 5px;
                        border-width: thin;
                    }

                    .shift-selected{
                        background-color: rgb(255, 255, 255);
                    }

                    .shift-disabled {
                        background-color: rgb(237, 237, 237) !important;
                    }

                    .shift-unallocated {
                        background-color: rgb(242, 242, 242) !important;
                    }

                    .faded-cell.shift-disabled {
                        background-color: rgba(237, 237, 237, 0.2) !important;
                    }

                    .shift-not-selected {
                        background-color: rgb(237, 237, 237);
                    }

                    .faded-cell.shift-not-selected {
                        background-color: rgba(237, 237, 237, 0.2);
                    }

                    .order_plan_color1 {
                        background-color: rgba(71, 163, 243, 0.102);
                    }

                    .order_plan_color2 {
                        background-color: rgba(23, 139, 240, 0.102);
                    }

                    .order_work_done {
                        background-color: rgba(154, 165, 177, 0.4);
                    }

                    .tool_setup {
                        background-image: url("data:image/svg+xml,%3Csvg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' x='0' y='0' viewBox='0 0 54 36' style='enable-background:new 0 0 54 36' xml:space='preserve'%3E%3Cstyle%3E.st0%7Bfill:%237b8794%7D%3C/style%3E%3Cpath class='st0' d='M53 30h1l-6 6v-1zM54 35v1h-1zM53 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M54 29v1h-1zM53 18h1l-6 6v-1zM54 23v1h-1zM53 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M54 17v1h-1zM53 6h1l-6 6v-1zM54 11v1h-1zM53 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M54 5v1h-1zM47 30h1l-6 6v-1zM48 35v1h-1zM47 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M48 29v1h-1zM47 18h1l-6 6v-1zM48 23v1h-1zM47 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M48 17v1h-1zM47 6h1l-6 6v-1zM48 11v1h-1zM47 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M48 5v1h-1zM41 30h1l-6 6v-1zM42 35v1h-1zM41 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M42 29v1h-1zM41 18h1l-6 6v-1zM42 23v1h-1zM41 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M42 17v1h-1zM41 6h1l-6 6v-1zM42 11v1h-1zM41 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M42 5v1h-1zM35 30h1l-6 6v-1zM36 35v1h-1zM35 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M36 29v1h-1zM35 18h1l-6 6v-1zM36 23v1h-1zM35 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M36 17v1h-1zM35 6h1l-6 6v-1zM36 11v1h-1zM35 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M36 5v1h-1zM29 30h1l-6 6v-1zM30 35v1h-1zM29 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M30 29v1h-1zM29 18h1l-6 6v-1zM30 23v1h-1zM29 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M30 17v1h-1zM29 6h1l-6 6v-1zM30 11v1h-1zM29 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M30 5v1h-1zM23 30h1l-6 6v-1zM24 35v1h-1zM23 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M24 29v1h-1zM23 18h1l-6 6v-1zM24 23v1h-1zM23 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M24 17v1h-1zM23 6h1l-6 6v-1zM24 11v1h-1zM23 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M24 5v1h-1zM17 30h1l-6 6v-1zM18 35v1h-1zM17 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M18 29v1h-1zM17 18h1l-6 6v-1zM18 23v1h-1zM17 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M18 17v1h-1zM17 6h1l-6 6v-1zM18 11v1h-1zM17 0h1l-6 6V5z'/%3E%3Cpath class='st0' d='M18 5v1h-1zM11 30h1l-6 6v-1zM12 35v1h-1zM11 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M12 29v1h-1zM11 18h1l-6 6v-1zM12 23v1h-1zM11 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M12 17v1h-1zM11 6h1l-6 6v-1zM12 11v1h-1zM11 0h1L6 6V5z'/%3E%3Cpath class='st0' d='M12 5v1h-1zM5 30h1l-6 6v-1zM6 35v1H5zM5 24h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M6 29v1H5zM5 18h1l-6 6v-1zM6 23v1H5zM5 12h1l-6 6v-1z'/%3E%3Cpath class='st0' d='M6 17v1H5zM5 6h1l-6 6v-1zM6 11v1H5zM5 0h1L0 6V5z'/%3E%3Cpath class='st0' d='M6 5v1H5z'/%3E%3C/svg%3E");
                        background-size: 54px 36px;
                    }

                    .microplan-warning-events-image {
                        position: absolute;
                        width: 9px;
                        height: 35px;
                        overflow: hidden;
                    }

                    .microplan-warning-events-image:after {
                        content: "";
                        display: block;
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100%;
                        height: 100%;
                        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 7 28'%3E%3Cdefs%3E%3Cstyle%3E.cls-2%7Bfill:%23ffe3e3%7D%3C/style%3E%3C/defs%3E%3Cpath style='fill:%23f86a6a' d='M0 0h7v28H0z'/%3E%3Cpath class='cls-2' d='M2.5 9h2v6h-2zM2.5 17h2v2h-2z'/%3E%3C/svg%3E");
                        background-position: center;
                        background-size: cover;
                    }

                    @media print { body { -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body onload="window.print()">
                ${print_timestamp ? `<p>${niceDateTime(new Date())}</p>` : ""}
                ${html_string}
            </body>
        </html>
    `;

    createHtmlAndDownload(html, "microplan.html");
}

const filterWorkOrderColumns = (columns: string[] | null): string[] => {
    if (!columns) {
        return [];
    }

    const valid_columns = Object.keys(WORK_ORDER_TABLE_COLUMNS);
    const filtered_columns: string[] = [];
    for (const column of columns) {
        if (valid_columns.includes(column)) {
            filtered_columns.push(column);
        }
    }

    return filtered_columns;
};

type PrintSettings = {
    work_order_columns: string[];
    work_order_shifts_limit: number;
    print_timestamp: boolean;
};

const getPrintSettings = (): PrintSettings => {
    const print_settings: PrintSettings = {
        work_order_columns: [],
        work_order_shifts_limit: 0,
        print_timestamp: false,
    };

    try {
        const settings_json = local_settings.getItem(PRINT_SETTINGS_KEY);
        if (!settings_json) {
            return print_settings;
        }

        const settings = JSON.parse(settings_json) as Partial<PrintSettings>;
        if (Array.isArray(settings.work_order_columns)) {
            print_settings.work_order_columns = filterWorkOrderColumns(settings.work_order_columns);
        }

        if (settings.work_order_shifts_limit !== undefined) {
            print_settings.work_order_shifts_limit = settings.work_order_shifts_limit;
        }

        if (settings.print_timestamp !== undefined) {
            print_settings.print_timestamp = settings.print_timestamp;
        }
    } catch (err) {
        console.log(err);
    }

    return print_settings;
};

const storePrintSettings = (settings: PrintSettings) => {
    try {
        local_settings.setItem(
            PRINT_SETTINGS_KEY,
            JSON.stringify({
                ...settings,
                work_order_columns: filterWorkOrderColumns(settings.work_order_columns),
            })
        );
    } catch (err) {
        console.log(err);
    }
};

type PrintModalProps = {
    export_type: string;
    show: boolean;
    show_material: boolean;
    show_material_comment: boolean;
    show_order_id: boolean;
    show_operation: boolean;
    show_operation_comment: boolean;
    show_expected_start: boolean;
    show_expected_completion: boolean;
    show_alternative_unit: boolean;
    show_time_to_complete: boolean;
    show_custom_field: boolean;
    show_quantity: boolean;
    show_sap_dates: boolean;
    show_order_shifts: boolean;
    onPrint: (state: PrintModalState) => void;
    onHide: () => void;
};

type PrintModalState = {
    selected_columns: string[];
    shifts_limit: number;
    print_timestamp: boolean;
};

class PrintModal extends React.Component<PrintModalProps, PrintModalState> {
    constructor(props: PrintModalProps) {
        super(props);
        this.state = {
            selected_columns: [],
            shifts_limit: 0,
            print_timestamp: false,
        };
    }

    componentDidMount() {
        this.initPrintSettings();
    }

    componentDidUpdate(prev_props: PrintModalProps) {
        const {
            show_material,
            show_material_comment,
            show_order_id,
            show_operation,
            show_operation_comment,
            show_expected_start,
            show_expected_completion,
            show_alternative_unit,
            show_time_to_complete,
            show_custom_field,
            show_quantity,
            show_sap_dates,
            show_order_shifts,
        } = this.props;
        if (
            show_material !== prev_props.show_material ||
            show_material_comment !== prev_props.show_material_comment ||
            show_order_id !== prev_props.show_order_id ||
            show_operation !== prev_props.show_operation ||
            show_operation_comment !== prev_props.show_operation_comment ||
            show_expected_start !== prev_props.show_expected_start ||
            show_expected_completion !== prev_props.show_expected_completion ||
            show_alternative_unit !== prev_props.show_alternative_unit ||
            show_time_to_complete !== prev_props.show_time_to_complete ||
            show_custom_field !== prev_props.show_custom_field ||
            show_quantity !== prev_props.show_quantity ||
            show_sap_dates !== prev_props.show_sap_dates ||
            show_order_shifts !== prev_props.show_order_shifts
        ) {
            this.initPrintSettings();
        }
    }

    getEnabledColumns = (): string[] => {
        const columns: string[] = [];

        for (const column of Object.keys(WORK_ORDER_TABLE_COLUMNS)) {
            const skip =
                (!this.props.show_material && column === WORK_ORDER_TABLE_COLUMNS.material) ||
                (!this.props.show_material_comment && column === WORK_ORDER_TABLE_COLUMNS.material_comment) ||
                (!this.props.show_order_id && column === WORK_ORDER_TABLE_COLUMNS.order_id) ||
                (!this.props.show_operation && column === WORK_ORDER_TABLE_COLUMNS.operation) ||
                (!this.props.show_operation_comment && column === WORK_ORDER_TABLE_COLUMNS.operation_comment) ||
                (!this.props.show_expected_start && column === WORK_ORDER_TABLE_COLUMNS.expected_start) ||
                (!this.props.show_expected_completion && column === WORK_ORDER_TABLE_COLUMNS.expected_completion) ||
                (!this.props.show_alternative_unit && column === WORK_ORDER_TABLE_COLUMNS.quantity_in_operation_unit) ||
                (!this.props.show_time_to_complete && column === WORK_ORDER_TABLE_COLUMNS.time_to_complete) ||
                (!this.props.show_custom_field && column === WORK_ORDER_TABLE_COLUMNS.custom_field) ||
                (!this.props.show_quantity && column === WORK_ORDER_TABLE_COLUMNS.quantity) ||
                (!this.props.show_sap_dates &&
                    (column === WORK_ORDER_TABLE_COLUMNS.sap_start ||
                        column === WORK_ORDER_TABLE_COLUMNS.sap_completion)) ||
                (this.props.show_order_shifts &&
                    (column === WORK_ORDER_TABLE_COLUMNS.expected_start ||
                        column === WORK_ORDER_TABLE_COLUMNS.expected_completion));
            if (!skip) {
                columns.push(column);
            }
        }

        return columns;
    };

    initPrintSettings = () => {
        const settings = getPrintSettings();
        const stored_columns = settings.work_order_columns;
        const selected_columns = stored_columns.length
            ? this.getEnabledColumns().filter(c => stored_columns.includes(c))
            : this.getEnabledColumns();
        this.setState({
            selected_columns,
            shifts_limit: settings.work_order_shifts_limit,
            print_timestamp: settings.print_timestamp,
        });
    };

    translateColumn = (column: string): string => {
        const tr = {
            material: translate("common.material", "Material"),
            material_comment: translate("common.material_comment", "Material comment"),
            order_id: translate("OrderTableProduction.order_id", "Order ID"),
            operation: translate("common.operation", "Operation"),
            operation_comment: translate("common.comment", "Comment"),
            sap_start: translate("OrderTableProduction.earliest_start", "ERP start"),
            sap_completion: translate("OrderTableProduction.earliest_end", "ERP completion"),
            expected_start: translate("OrderTableProduction.predicted_start", "Expected start"),
            expected_completion: translate("OrderTableProduction.predicted_end", "Expected completion"),
            time_to_complete: translate("OrderTableProduction.time_to_complete", "Time to complete [h]"),
            custom_field: translate("OrderTableProduction.custom_field", "Custom field"),
            quantity: translate("OrderTableProduction.done_plan_quant", "Produced/Target quantity"),
            quantity_in_operation_unit: translate(
                "OrderTableProduction.quantity_in_operation_unit",
                "Quantity in operation measurement unit"
            ),
        };
        return tr[column] || column;
    };

    isColumnSelected = (column: string) => {
        return this.state.selected_columns.indexOf(column) >= 0;
    };

    handlePrintClick = () => {
        const settings = getPrintSettings();
        const stored_columns = settings.work_order_columns;
        const enabled_columns = this.getEnabledColumns();
        const { selected_columns } = this.state;
        for (const column of enabled_columns) {
            const i = stored_columns.indexOf(column);
            if (selected_columns.includes(column)) {
                if (i < 0) {
                    stored_columns.push(column);
                }
            } else {
                if (i >= 0) {
                    stored_columns.splice(i, 1);
                }
            }
        }

        storePrintSettings({
            work_order_columns: stored_columns,
            work_order_shifts_limit: this.state.shifts_limit,
            print_timestamp: this.state.print_timestamp,
        });
        this.props.onPrint(this.state);
    };

    handleColumnCheckboxChange = (event: SyntheticEvent<HTMLInputElement>) => {
        const target = event.currentTarget;
        this.setState(prev_state => {
            const work_order_columns = prev_state.selected_columns.filter(c => c !== target.value);
            if (target.checked || work_order_columns.length < 1) {
                work_order_columns.push(target.value);
            }

            return {
                ...prev_state,
                selected_columns: work_order_columns,
            };
        });
    };

    handleShiftsLimitChange = (event: SyntheticEvent<HTMLInputElement>) => {
        const value = Number(event.currentTarget.value);
        this.setState({
            shifts_limit: Number.isNaN(value) ? 0 : value,
        });
    };

    handleInputChange = (event: SyntheticEvent<HTMLInputElement>) => {
        const target: HTMLInputElement = event.currentTarget;
        if (target.type === "checkbox") {
            this.setState({
                print_timestamp: target.checked,
            } as Pick<PrintModalState, keyof PrintModalState>);
        }
    };

    renderGeneralSettings = () => {
        return (
            <fieldset
                className={classNames("form-group", {
                    "mb-0": this.props.export_type === MICROPLAN_EXPORT_TYPE.shifts,
                })}
            >
                <legend className="h6 pt-0">{translate("common.general", "General")}</legend>
                <div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            name="print_timestamp"
                            id="print-timestamp-checkbox"
                            checked={this.state.print_timestamp}
                            onChange={this.handleInputChange}
                        />
                        <label className="form-check-label" htmlFor="print-timestamp-checkbox">
                            {translate("common.print_timestamp", "Print timestamp")}
                        </label>
                    </div>
                </div>
            </fieldset>
        );
    };

    renderColumnCheckbox = (column: string) => {
        const id = `${column}_checkbox`;
        return (
            <div key={id} className="form-check">
                <input
                    className="form-check-input"
                    type="checkbox"
                    name={column}
                    id={id}
                    value={column}
                    checked={this.isColumnSelected(column)}
                    onChange={this.handleColumnCheckboxChange}
                />
                <label className="form-check-label" htmlFor={id}>
                    {this.translateColumn(column)}
                </label>
            </div>
        );
    };

    renderColumnSettings = () => {
        return (
            <fieldset className="form-group">
                <legend className="h6 pt-0">
                    {translate("OrderTableProduction.workorders_table_columns", "Work-orders table columns")}
                </legend>
                <div>{this.getEnabledColumns().map(col => this.renderColumnCheckbox(col))}</div>
            </fieldset>
        );
    };

    renderShiftSettings = () => {
        return (
            <fieldset className="form-group mb-0">
                <legend className="h6 pt-0">{translate("OrderTableProduction.workorders", "Work-orders")}</legend>
                <div>
                    <label htmlFor="work-order-shifts-limit">
                        {translate("common.number_of_shifts", "Number of shifts")}
                    </label>
                    <input
                        className="form-control"
                        id="work-order-shifts-limit"
                        type="number"
                        name="shifts-limit"
                        value={this.state.shifts_limit || ""}
                        onChange={this.handleShiftsLimitChange}
                        min={0}
                    />
                </div>
            </fieldset>
        );
    };

    render() {
        const export_shifts = this.props.export_type === MICROPLAN_EXPORT_TYPE.shifts;
        return (
            <Modal size="sm" show={this.props.show} onHide={this.props.onHide}>
                <Modal.Header>
                    <Modal.Title>{translate("common.print", "Print")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form>
                        {this.renderGeneralSettings()}
                        {!export_shifts && this.renderColumnSettings()}
                        {!export_shifts && this.renderShiftSettings()}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={this.props.onHide} className="btn btn-outline-primary">
                        {translate("common.cancel", "Cancel")}
                    </button>
                    <button onClick={this.handlePrintClick} className="btn btn-outline-primary">
                        {translate("common.print", "Print")}
                    </button>
                </Modal.Footer>
            </Modal>
        );
    }
}

const loadMicroplanOrderTypes = (available_order_types: string[]): string[] => {
    let order_types = available_order_types;
    const order_types_set = new Set(order_types);
    const order_types_json = local_settings.getItem(FIELD_MICROPLAN_ORDER_TYPES);
    if (order_types_json != null) {
        try {
            const ls_order_types = JSON.parse(order_types_json);
            if (Array.isArray(ls_order_types)) {
                const stored_order_types: string[] = [];
                for (const ls_order_type of ls_order_types) {
                    const order_type = String(ls_order_type);
                    if (order_types_set.has(order_type)) {
                        stored_order_types.push(order_type);
                    }
                }

                order_types = stored_order_types;
            }
        } catch (error) {
            console.error(error);
        }
    }

    return order_types;
};

const storeMicroplanOrderTypes = (order_types: string[]) => {
    local_settings.setItem(FIELD_MICROPLAN_ORDER_TYPES, JSON.stringify(order_types));
};

const mapOrderTypeToLabelValue = (order_type: string): ILabelValuePair => {
    return {
        label: translate(`order_type.${order_type}`, order_type),
        value: order_type,
    };
};

const mapOrderTypesToLabelValue = (order_types: string[]): ILabelValuePair[] => {
    return order_types.map(mapOrderTypeToLabelValue);
};

const ShiftTableToolbar = (props: {
    is_loading: boolean;
    last_refresh_date?: number;
    available_order_types: string[];
    selected_order_types: string[];
    show_order_type_filter: boolean;
    onOrderTypesChange: (order_types: string[]) => void;
}) => {
    const {
        is_loading,
        last_refresh_date,
        available_order_types,
        selected_order_types,
        show_order_type_filter,
        onOrderTypesChange,
    } = props;
    const options_container = document.querySelector("#shift-table-toolbar");

    if (!show_order_type_filter && !last_refresh_date) {
        return null;
    }

    const options = (
        <div className="form-row">
            {last_refresh_date && (
                <div className="form-group col-auto d-flex align-items-center mb-0">
                    {translate("common.last_update", "Last update")}: {niceDateTime(last_refresh_date)}
                </div>
            )}
            {show_order_type_filter && (
                <div className="form-group col-auto ml-auto mr-0 mb-0">
                    <MultiSelect
                        size="sm"
                        name="order_type"
                        placeholder={translate("OrderTableProduction.order_type", "Order type")}
                        value={mapOrderTypesToLabelValue(selected_order_types)}
                        options={mapOrderTypesToLabelValue(available_order_types)}
                        onChange={(selected: ILabelValuePair[]) => onOrderTypesChange(selected.map(s => s.value))}
                        isClearable={false}
                        isLoading={is_loading}
                        enable_select_all
                    />
                </div>
            )}
        </div>
    );

    return options_container !== null ? (
        createPortal(options, options_container)
    ) : (
        <div className="shift-table-toolbar white_box py-2 mb-3">{options}</div>
    );
};

export type DataDef = {
    filters: {
        plant: string;
        line_groups: string[];
        lines: string[];
        weeks: number[];
    };
    weeks: number[];
    line_group_uuid: string;
    line_group_title: string;
};

type OwnProps = {
    history: History;
    data_def: DataDef;
    data: { source_type: GanttChartSourceTypes };
    show_title: boolean;
    show_line_gantt: boolean;
};

type Props = OwnProps & ReduxProps;

type ShiftTableLine = {
    uuid: string;
    title: string;
};

export type OrderShift = {
    shift_time: rrt.ISimulationReportShiftTime;
    quantity_predicted: number;
    tool_setup: boolean;
};

type State = {
    // displayed lines
    plant: string;
    line_group_uuids: string[];
    lines: ShiftTableLine[];
    line_uuids: string[];
    filtered_order_uuids?: string[];
    week_offsets: number[];
    simulation: rt.IReportModelEx | null;
    line_orders: Map<string, rt.ISimulationReportOrderEx[]>;
    line_unscheduled_orders: Map<string, t.IOrderProducedModel[]>;
    line_order_shifts: Map<string, Map<string, OrderShift[]>>;
    loaded_simulation: boolean;
    weekly_realization: Map<number, WeeklyReport[]>;
    loaded_weekly_realization: boolean;
    insights: Map<string, Map<number, InsightWeekShiftGrid>>;
    loaded_insights: boolean;
    // widget parameters
    show_title: boolean;
    show_line_gantt: boolean;
    // line and line-group parameters
    show_shift_table: boolean;
    show_order_table: boolean;
    // modal dialogs
    line_uuid: string;
    line_title: string;
    show_schedule_modal: boolean;
    show_insight_modal: boolean;
    show_material_modal: boolean;
    show_print_modal: boolean;
    display_type: MaterialForecastType;
    materials: t.IMaterialModel[];
    operations: t.IOrderModelBase[];
    export_type: string;
    available_order_types: string[];
    selected_order_types: string[];
    line_statuses_map: Map<string, t.ILineStatusItem | Error>;
};

class ShiftTableStandalone extends React.Component<Props, State> {
    nodeRef = React.createRef<HTMLDivElement>();

    state: State = {
        plant: "",
        line_group_uuids: [],
        lines: [],
        line_uuids: [],
        week_offsets: [-1, 0, 1, 2],
        simulation: null,
        line_orders: new Map(),
        line_unscheduled_orders: new Map(),
        line_order_shifts: new Map(),
        loaded_simulation: false,
        weekly_realization: new Map(),
        loaded_weekly_realization: false,
        insights: new Map(),
        loaded_insights: false,
        show_title: false,
        show_line_gantt: false,
        show_shift_table: true,
        show_order_table: true,
        line_uuid: "",
        line_title: "",
        show_schedule_modal: false,
        show_insight_modal: false,
        show_material_modal: false,
        show_print_modal: false,
        display_type: "in",
        materials: [],
        operations: [],
        export_type: "",
        available_order_types: [],
        selected_order_types: [],
        line_statuses_map: new Map(),
    };

    refresh_timeout?: ReturnType<typeof setInterval>;

    async componentDidMount() {
        const el = this.nodeRef.current;

        if (el) {
            el.addEventListener("export-html-microplan", this.handleExportHTML);
        }

        try {
            // parse parameters from the properties
            const data_def = this.props.data_def;
            if (data_def && data_def.filters) {
                const plant = data_def.filters.plant;
                const line_group_uuids = data_def.filters.line_groups;
                const line_uuids = data_def.filters.lines;
                let week_offsets = this.state.week_offsets;
                let show_shift_table = this.state.show_shift_table;
                let show_order_table = this.state.show_order_table;
                if (data_def.filters.line_groups && data_def.filters.line_groups.length > 0) {
                    const line_group_uuid = data_def.filters.line_groups[0];
                    week_offsets = BusinessLogic.getLineGroupTagJson(line_group_uuid, "microplan_weeks", week_offsets);
                    show_shift_table = BusinessLogic.getLineGroupTagBool(
                        line_group_uuid,
                        "microplan_show_shift_table",
                        show_shift_table
                    );
                    show_order_table = BusinessLogic.getLineGroupTagBool(
                        line_group_uuid,
                        "microplan_show_order_table",
                        show_order_table
                    );
                }
                if (data_def.weeks && data_def.weeks.length > 0) {
                    week_offsets = data_def.weeks;
                }

                this.setState({
                    plant,
                    line_group_uuids,
                    line_uuids,
                    week_offsets,
                    show_shift_table,
                    show_order_table,
                });
                await this.loadSimulation(line_uuids, week_offsets, plant);
            }
            // display parameters
            const show_title = this.props.show_title === true;
            const show_line_gantt = this.props.show_line_gantt === true;
            this.setState({
                show_title,
                show_line_gantt,
            });
            this.refreshReportAfter();
        } catch (err) {
            console.log(err);
            this.setState({
                loaded_simulation: true,
                loaded_weekly_realization: true,
                loaded_insights: true,
            });
        }
    }

    componentDidUpdate(prev_props: Props) {
        if (prev_props.line_group_uuid !== this.props.line_group_uuid) {
            clearInterval(this.refresh_timeout);
            this.refreshReportAfter();
        }
    }

    componentWillUnmount() {
        clearInterval(this.refresh_timeout);

        const el = this.nodeRef.current;

        if (el) {
            el.removeEventListener("export-html-microplan", this.handleExportHTML);
        }
    }

    async refresh() {
        const { line_uuids, week_offsets, plant } = this.state;

        const simulation = await getGanttDataSimpleAsync(null, line_uuids);

        if (this.state.simulation === null || this.state.simulation.uuid !== simulation.uuid) {
            await this.processSimulation(simulation, line_uuids, week_offsets, plant);
            this.setState({ loaded_simulation: true });
        }
    }

    refreshReportAfter() {
        const microplan_refresh_minutes = BusinessLogic.getLineGroupTagInt(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.microplan_refresh_minutes,
            -1
        );
        if (microplan_refresh_minutes !== -1) {
            this.refresh_timeout = setInterval(() => this.refresh(), TIME_RANGES.MINUTE * microplan_refresh_minutes);
        }
    }

    async loadSimulation(line_uuids: string[], week_offsets: number[], plant: string): Promise<void> {
        const simulation = await getGanttDataSimpleAsync(null, line_uuids);
        if (simulation.translations && simulation.translations.materials.length > 0) {
            this.props.reduxDispatch({
                type: SET_TRANSLATIONS_MATERIALS,
                data: simulation.translations.materials,
            });
        }
        await this.processSimulation(simulation, line_uuids, week_offsets, plant);
        this.setState({ loaded_simulation: true });
    }

    async processSimulation(
        simulation: rt.IReportModelEx,
        line_uuids: string[],
        week_offsets: number[],
        plant: string
    ): Promise<void> {
        this.setState({ simulation });
        // get time of the simulation
        const year = simulation.result.next_shift_time.year;
        const week = simulation.result.next_shift_time.week;
        // extract orders from simulation
        await this.loadOrders(simulation, line_uuids);
        // collect insights and realization for each week
        const tasks: Promise<void>[] = [];
        for (const week_offset of week_offsets) {
            const week_year = shiftNumber(dateFromWeekAndShift(week + week_offset, year, 0));
            const current_week = week_year.week;
            const current_year = week_year.year;
            tasks.push(this.loadRealization(current_week, current_year, plant, line_uuids));
            tasks.push(this.loadInsights(current_week, current_year, line_uuids));
        }
        await Promise.all(tasks);
        this.setState({ loaded_weekly_realization: true, loaded_insights: true });
    }

    async loadOrders(simulation: rt.IReportModelEx, line_uuids: string[]) {
        const lines: ShiftTableLine[] = [];
        const line_orders: Map<string, rt.ISimulationReportOrderEx[]> = new Map();
        const order_produced: Map<string, number> = new Map();
        const material_ids: Set<string> = new Set();
        const order_uuids: Set<string> = new Set();
        const order_types: Set<string> = new Set();
        for (const line of simulation.result.orders) {
            if (line.plant !== this.state.plant) {
                continue;
            }
            if (line_uuids.includes(line.line)) {
                // remember line with all the details
                lines.push({
                    uuid: line.line,
                    title: line.line_title,
                });
                // remember line orders
                const orders: rt.ISimulationReportOrderEx[] = [];
                for (const order of line.production) {
                    order.line_uuid = line.line;
                    orders.push(order);
                    order_uuids.add(order.order_id);
                    order_types.add(order.order_type);
                    // remember already produced quantity
                    order_produced.set(order.order_id, order.quantity_produced_fixed);
                    material_ids.add(order.material);
                }
                line_orders.set(line.line, orders);
            }
        }
        const line_order_shifts: Map<string, Map<string, OrderShift[]>> = new Map();
        for (const line of simulation.result.line_shift_features) {
            if (line_orders.has(line.line)) {
                const order_shifts: Map<string, OrderShift[]> = new Map();
                for (const shift of line.shifts) {
                    for (const order of shift.produced) {
                        const shifts = order_shifts.get(order.order_id) || [];
                        // compute the amount produced in this shift, to do that we keep track
                        // of production so far, since sums in the report are cumulative
                        const quantity_produced = order_produced.get(order.order_id) || 0;
                        // if more quantiles take second
                        const total_quantity_predicted =
                            order.quantity_predicted.length > 1
                                ? order.quantity_predicted[1].val
                                : order.quantity_predicted.length > 0
                                  ? order.quantity_predicted[0].val
                                  : 0.0;
                        const quantity_predicted = total_quantity_predicted - quantity_produced;
                        // only remember nonzero values exect at start when we do setup
                        if (quantity_predicted > 0 || total_quantity_predicted === 0) {
                            order_produced.set(order.order_id, total_quantity_predicted);
                            // remember shift for the order
                            shifts.push({
                                shift_time: shift.shift_time,
                                quantity_predicted,
                                tool_setup: order.tool_setup,
                            });
                            order_shifts.set(order.order_id, shifts);
                        }
                    }
                }
                line_order_shifts.set(line.line, order_shifts);
            }
        }

        const line_unscheduled_orders: Map<string, t.IOrderProducedModel[]> = new Map();
        const selected_line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        if (
            selected_line_group !== null &&
            LINE_GROUP_TAGS_ACCESS.microplan_show_order_table_unscheduled_orders(selected_line_group.tags)
        ) {
            const res2 = await getBackend2().manufacturing.searchOrders({
                line_uuids: line_uuids,
                order_types: ["full"],
                status: "open",
                skip_sim: true,
            });
            for (const order of res2.orders) {
                let line_orders = line_unscheduled_orders.get(order.line_uuid);
                if (line_orders === undefined) {
                    line_orders = [];
                    line_unscheduled_orders.set(order.line_uuid, line_orders);
                }

                line_orders.push(order);
                material_ids.add(order.material_external_id);
            }
        }

        const res = await getBackend2().manufacturing.searchMaterialsSimple({ external_ids: [...material_ids] });
        const materials = res.data;

        const { operations } = await getBackend2().manufacturing.getOrdersSimple({ uuids: [...order_uuids] });
        const available_order_types = [...order_types];

        const line_statuses_map = await ReportLogic.fetchLineStatusesMap(lines.map(l => l.uuid));

        this.setState({
            available_order_types,
            selected_order_types: loadMicroplanOrderTypes(available_order_types),
            lines,
            line_orders,
            line_unscheduled_orders,
            line_order_shifts,
            materials,
            operations,
            line_statuses_map,
        });
    }

    async loadRealization(current_week: number, year: number, plant: string, line_uuids: string[]): Promise<void> {
        // cache simulation reports when preparing weekly realization report export
        const sim_report_add_to_cache = true;
        try {
            const result = await loadRealization(current_week, year, plant, line_uuids, true, sim_report_add_to_cache);
            this.setState(state => {
                state.weekly_realization.set(current_week, result.data);
                return state;
            });
        } catch (e) {
            console.log(e);
            throw e;
        }
    }

    async loadInsights(current_week: number, year: number, line_uuids: string[]): Promise<void> {
        // get insights
        const from = dateFromWeekAndShift(current_week, year, 0).getTime();
        const params: t.IGetInsightsReq = {
            from: from,
            to: from + TIME_RANGES.WEEK,
            types: [INSIGHT_TYPES.man_downtime, INSIGHT_TYPES.tool_setup, INSIGHT_TYPES.no_work_in_shift],
            lines: line_uuids,
        };
        const insights_report = await getBackend2().manufacturing.getInsights(params);
        this.setState(state => {
            // Iterate over insights and construct eventsGrid for each line
            for (const line_uuid of line_uuids) {
                const line_insights = insights_report.insights.filter(insight => insight.tags.line_uuid === line_uuid);
                const eventsGrid = eventsIntoWeekShiftsGrid(line_insights);
                const line_week_insights = state.insights.has(line_uuid) ? state.insights.get(line_uuid) : new Map();
                if (line_week_insights) {
                    // have to check to keep flow happy
                    line_week_insights.set(current_week, eventsGrid);
                    state.insights.set(line_uuid, line_week_insights);
                }
            }
            return state;
        });
    }

    handleOrderChange = (order: t.IOrderModelBase) => {
        let i = this.state.operations.findIndex(o => o.uuid === order.uuid);
        if (i >= 0) {
            const operations = [...this.state.operations];
            operations.splice(i, 1, order);
            this.setState({ operations });
            return;
        }

        const { line_unscheduled_orders } = this.state;
        const unscheduled_orders = line_unscheduled_orders.get(order.line.uuid);
        if (unscheduled_orders === undefined) {
            return;
        }

        i = unscheduled_orders.findIndex(o => o.uuid === order.uuid);
        if (i >= 0) {
            const unscheduled_order = unscheduled_orders[i];
            unscheduled_order.tags = {
                ...unscheduled_order.tags,
                ...order.tags,
            };
            unscheduled_orders.splice(i, 1, unscheduled_order);
            line_unscheduled_orders.set(order.line.uuid, unscheduled_orders);
            this.setState({ line_unscheduled_orders });
        }
    };

    getLineOrders(line_uuid: string): rt.ISimulationReportOrderEx[] {
        const line_orders = this.state.line_orders.get(line_uuid);
        return line_orders !== undefined ? line_orders : [];
    }

    getTableSettings = (line_group_uuid?: string) => {
        let show_alternative_unit = false;
        let show_expected_completion = true;
        let show_expected_start = true;
        let show_external_operation_id = false;
        let show_material = true;
        let show_material_comment = false;
        let show_operation = true;
        let show_operation_comment = false;
        let show_order_id = true;
        let show_quantity = true;
        let show_sap_dates = true;
        let show_shifts = false;
        let show_time_to_complete = true;
        let show_custom_field = false;

        if (line_group_uuid) {
            show_alternative_unit = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_alternative_unit,
                show_alternative_unit
            );
            show_expected_completion = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_expected_completion,
                show_expected_completion
            );
            show_expected_start = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_expected_start,
                show_expected_start
            );
            show_external_operation_id = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_external_operation_id,
                show_external_operation_id
            );
            show_material = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_material,
                show_material
            );
            show_material_comment = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_material_comment,
                show_material_comment
            );
            show_operation = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_operation,
                show_operation
            );
            show_operation_comment = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_operation_comment,
                show_operation_comment
            );
            show_order_id = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_order_id,
                show_order_id
            );
            show_quantity = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_quantity,
                show_quantity
            );
            show_sap_dates = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_sap_dates,
                show_sap_dates
            );
            show_shifts = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_shifts,
                show_shifts
            );
            show_time_to_complete = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_time_to_complete,
                show_time_to_complete
            );
            show_custom_field = BusinessLogic.getLineGroupTagBool(
                line_group_uuid,
                LINE_GROUP_TAGS.microplan_show_order_table_custom_field,
                show_custom_field
            );
        }

        return {
            show_alternative_unit,
            show_expected_completion,
            show_expected_start,
            show_external_operation_id,
            show_material_comment,
            show_material,
            show_operation_comment,
            show_operation,
            show_order_id,
            show_quantity,
            show_sap_dates,
            show_shifts,
            show_time_to_complete,
            show_custom_field,
        };
    };

    getSchedulingHorizon = (): number => {
        const { simulation } = this.state;
        if (simulation === null) {
            return 0;
        }

        const { shift_number, week_number, year } = simulation.input.data.next_shift_time;
        const horizon = addHours(
            dateFromWeekAndShift(week_number, year, shift_number),
            (simulation.input.data.analysis_configuration.predict_shifts ?? 0) * SHIFT_CONSTS.SHIFT_DURATION_HOURS
        );

        return horizon.getTime();
    };

    getCustomFieldTitle = (): string => {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        if (line_group !== null) {
            return LINE_GROUP_TAGS_ACCESS.microplan_order_table_custom_field_title(line_group.tags);
        }

        return "";
    };

    isShowOrderTypesFilterEnabled = () => {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        return line_group !== null && LINE_GROUP_TAGS_ACCESS.microplan_show_order_type_filter(line_group.tags);
    };

    filterOrders<T extends { order_type: string }>(orders: T[]): T[] {
        let filtered_orders: T[] = orders;
        if (this.isShowOrderTypesFilterEnabled()) {
            const order_types: Set<string> = new Set(this.state.selected_order_types);
            filtered_orders = orders.filter(order => order_types.has(order.order_type));
        }

        return filtered_orders;
    }

    ////////////////////////////////
    // Modal dialogs
    showLineSchedule = (line: ShiftTableLine) => {
        this.setState({
            line_uuid: line.uuid,
            line_title: line.title,
            show_schedule_modal: true,
            filtered_order_uuids: undefined,
        });
    };

    showLinePlanningModal = (line: ShiftTableLine) => (filtered_order_uuids: string[]) => {
        for (let i = 0; i < filtered_order_uuids.length; i++) {
            const order_uuid = filtered_order_uuids[i];
            const operation = this.state.operations.find(o => o.uuid === order_uuid);
            if (
                operation &&
                operation.tags &&
                operation.tags[ORDER_TAGS.parallel_order] === "true" &&
                operation.parent_order_uuid
            ) {
                filtered_order_uuids[i] = operation.parent_order_uuid;
            }
        }

        this.setState({
            line_uuid: line.uuid,
            line_title: line.title,
            filtered_order_uuids,
            show_schedule_modal: true,
        });
    };

    hideLineSchedule = () => {
        this.setState({ show_schedule_modal: false });
    };

    renderLineScheduleModal() {
        return (
            <LinePlanningModal
                history={this.props.history}
                show={this.state.show_schedule_modal}
                onHide={this.hideLineSchedule}
                line_uuid={this.state.line_uuid}
                line_title={this.state.line_title}
                filtered_order_uuids={this.state.filtered_order_uuids}
            />
        );
    }

    showLineInsights = (line: ShiftTableLine) => {
        this.setState({
            line_uuid: line.uuid,
            line_title: line.title,
            show_insight_modal: true,
        });
    };

    hideLineInsights = () => {
        this.setState({ show_insight_modal: false });
    };

    renderLineInsightsModal() {
        return (
            <LineInsightListModal
                history={this.props.history}
                show={this.state.show_insight_modal}
                onHide={this.hideLineInsights}
                line_uuid={this.state.line_uuid}
                line_title={this.state.line_title}
            />
        );
    }

    showLineMaterials = (line: ShiftTableLine, display_type: MaterialForecastType) => {
        this.setState({
            line_uuid: line.uuid,
            line_title: line.title,
            show_material_modal: true,
            display_type,
        });
    };

    hideLineMaterials = () => {
        this.setState({ show_material_modal: false });
    };

    renderLineMaterialsModal() {
        // get raw string
        const display_location_raw =
            this.state.line_group_uuids.length === 0
                ? MATERIAL_FORECAST_LOCATIONS.plant
                : BusinessLogic.getLineGroupTagStr(
                      this.state.line_group_uuids[0],
                      LINE_GROUP_TAGS.microplan_stock_forecast_location,
                      MATERIAL_FORECAST_LOCATIONS.plant
                  );
        // make sure one of the supported values, in case not default to shopfloor
        const display_location =
            display_location_raw === MATERIAL_FORECAST_LOCATIONS.plant
                ? MATERIAL_FORECAST_LOCATIONS.plant
                : MATERIAL_FORECAST_LOCATIONS.shopfloor;

        return (
            <LineMaterialsModal
                history={this.props.history}
                show={this.state.show_material_modal}
                onHide={this.hideLineMaterials}
                line_uuid={this.state.line_uuid}
                line_title={this.state.line_title}
                display_type={this.state.display_type}
                display_subtype={MATERIAL_FORECAST_SUBTYPES.combined}
                only_accounted_for_initial_stock={MATERIAL_FORECAST_ONLY_ACCOUNTED.yes}
                display_location={display_location}
            />
        );
    }

    renderHtmlExport = (print_settings: PrintModalState) => {
        const { export_type } = this.state;
        const print_all = export_type === MICROPLAN_EXPORT_TYPE.all;
        const print_shifts = print_all || export_type === MICROPLAN_EXPORT_TYPE.shifts;
        const print_orders = print_all || export_type === MICROPLAN_EXPORT_TYPE.orders;

        const sim = this.state.simulation;

        if (sim && sim.result && sim.result.next_shift_time) {
            const current_shift = sim.result.next_shift_time.shift_number;
            const current_week = sim.result.next_shift_time.week;
            const current_year = sim.result.next_shift_time.year;
            const current_timestmap = dateFromWeekAndShift(current_week, current_year, current_shift).getTime();
            const lines_components: React.ReactNode[] = [];
            const plant = this.props.data_def.filters.plant;
            const show_setup_code = BusinessLogic.getPlantTagBool(plant, PLANT_TAGS.microplan_show_setup_code, false);
            const show_barcode = BusinessLogic.getPlantTagBool(plant, PLANT_TAGS.show_barcode, false);

            for (const line of this.state.lines) {
                // get orders
                const line_orders = this.state.line_orders.get(line.uuid) || [];
                const line_order_shifts = this.state.line_order_shifts.get(line.uuid) || new Map();
                const line_unscheduled_orders = this.state.line_unscheduled_orders.get(line.uuid) || [];

                // get insights
                const line_insights = this.state.insights.get(line.uuid) || new Map();

                // check if we should render earliest dates
                const table_settings = this.getTableSettings(this.props.line_group_uuid);

                // render
                lines_components.push(
                    <React.Fragment key={line.uuid}>
                        <div className="pane">
                            <h5 className="pane-title">{line.title}</h5>
                            {this.state.show_shift_table && print_shifts && (
                                <PlanShiftTableProduction
                                    line_uuid={line.uuid}
                                    realization={this.state.weekly_realization}
                                    orders={line_orders}
                                    events={line_insights}
                                    current_shift={current_shift}
                                    week_offsets={this.state.week_offsets}
                                    week={current_week}
                                    year={current_year}
                                />
                            )}
                            {this.state.show_order_table && print_orders && (
                                <OrderTableProduction
                                    history={this.props.history}
                                    orders={this.filterOrders<rt.ISimulationReportOrderEx>(line_orders)}
                                    unscheduled_orders={this.filterOrders<t.IOrderProducedModel>(
                                        line_unscheduled_orders
                                    )}
                                    order_shifts={line_order_shifts}
                                    start_timestamp={current_timestmap}
                                    compact={false}
                                    earliest_dates={table_settings.show_sap_dates}
                                    show_order_shifts={table_settings.show_shifts}
                                    show_setup_code={show_setup_code}
                                    show_barcode={show_barcode}
                                    show_operation_comment={table_settings.show_operation_comment}
                                    show_material_comment={table_settings.show_material_comment}
                                    show_external_operation_id={table_settings.show_external_operation_id}
                                    show_alternative_unit={table_settings.show_alternative_unit}
                                    show_material={table_settings.show_material}
                                    show_order_id={table_settings.show_order_id}
                                    show_operation={table_settings.show_operation}
                                    show_expected_start={table_settings.show_expected_start}
                                    show_expected_completion={table_settings.show_expected_completion}
                                    show_time_to_complete={table_settings.show_time_to_complete}
                                    show_custom_field={table_settings.show_custom_field}
                                    show_quantity={table_settings.show_quantity}
                                    showLinePlanningModal={this.showLinePlanningModal(line)}
                                    line={line}
                                    materials={this.state.materials}
                                    operations={this.state.operations}
                                    selected_columns={print_settings.selected_columns}
                                    shifts_limit={print_settings.shifts_limit}
                                    render_to_string={true}
                                    sim_created_at={this.state.simulation ? this.state.simulation.created_at : null}
                                    scheduling_horizon={this.getSchedulingHorizon()}
                                    custom_field_title={this.getCustomFieldTitle()}
                                    report={this.state.simulation}
                                />
                            )}
                        </div>
                    </React.Fragment>
                );
            }

            // Schedule rendering to string to fix problem of asynchronus rendering of barcodes with ReactDOM.render.
            setTimeout(() => {
                exportMicroplanHTML(
                    renderToString(
                        <IntlProviderWrapper>
                            <React.Fragment>{lines_components}</React.Fragment>
                        </IntlProviderWrapper>
                    ),
                    print_settings.print_timestamp
                );
            }, 0);
        }
    };

    handleExportHTML = (event: Event) => {
        const export_type = event instanceof CustomEvent ? event.detail : MICROPLAN_EXPORT_TYPE.all;
        this.setState({
            show_print_modal: true,
            export_type,
        });
    };

    handlePrintModalPrint = (print_settings: PrintModalState) => {
        this.setState({
            show_print_modal: false,
        });
        this.renderHtmlExport(print_settings);
    };

    handlePrintModalHide = () => {
        this.setState({
            show_print_modal: false,
        });
    };

    handleOrderTypesChange = (order_types: string[]) => {
        storeMicroplanOrderTypes(order_types);
        this.setState({ selected_order_types: order_types });
    };

    ////////////////////////////////
    // Rendering
    renderTitle(line: ShiftTableLine) {
        const line_group = BusinessLogic.getLineGroupForUser(this.props.line_group_uuid);
        const plant_uuids = line_group?.plant_uuids ?? [];
        const show_wip_materials_button = plant_uuids.some(plant_uuid =>
            PLANT_TAGS_ACCESS.enable_wip_stock_tracking(BusinessLogic.getPlantTags(plant_uuid))
        );

        return (
            <div className="d-flex">
                <div className="button-list order-last ml-auto">
                    <Authorization.button
                        permission={PERMISSION_NAMES.OrdersAndShiftsEdit}
                        onClick={() => {
                            this.showLineSchedule(line);
                        }}
                        className="btn btn-outline-primary"
                        title={translate("common.tooltip_microplan_edit", "Edit shifts and orders")}
                    >
                        <FormattedMessage id="common.edit" defaultMessage="Edit" />
                    </Authorization.button>
                    <button
                        onClick={() => {
                            this.showLineInsights(line);
                        }}
                        className="btn btn-outline-primary"
                    >
                        <FormattedMessage id="common.insights" defaultMessage="Insights" />
                    </button>
                    <button
                        onClick={() => {
                            this.showLineMaterials(line, "in");
                        }}
                        className="btn btn-outline-primary"
                        title={translate(
                            "common.tooltip_microplan_materials_in",
                            "Show input materials stock forecast for this line"
                        )}
                    >
                        <FormattedMessage id="OrderTableProduction.materials_in" defaultMessage="Input materials" />
                    </button>
                    <button
                        onClick={() => {
                            this.showLineMaterials(line, "out");
                        }}
                        className="btn btn-outline-primary"
                        title={translate(
                            "common.tooltip_microplan_produced_materials",
                            "Show produced materials stock forecast for this line"
                        )}
                    >
                        <FormattedMessage id="OrderTableProduction.materials_out" defaultMessage="Produced materials" />
                    </button>
                    {show_wip_materials_button && (
                        <button
                            onClick={() => {
                                this.showLineMaterials(line, "wip");
                            }}
                            className="btn btn-outline-primary"
                            title={translate(
                                "common.tooltip_microplan_wip_materials",
                                "Show WIP materials stock forecast for this line"
                            )}
                        >
                            <FormattedMessage
                                id="Manufacturing.MaterialStockForecast.material_wip"
                                defaultMessage="WIP"
                            />
                        </button>
                    )}
                </div>

                <div className="order-first d-flex align-items-center gap-2 m-2">
                    {this.state.line_statuses_map.get(line.uuid) && (
                        <LineStatus status={this.state.line_statuses_map.get(line.uuid)!} r={15} className="mb-1" />
                    )}
                    <h5 className="pane-title mb-0">{line.title}</h5>
                </div>
            </div>
        );
    }

    renderLine(
        line: ShiftTableLine,
        line_orders: rt.ISimulationReportOrderEx[],
        line_unscheduled_orders: t.IOrderProducedModel[],
        line_order_shifts: Map<string, OrderShift[]>,
        line_insights: Map<number, InsightWeekShiftGrid>,
        current_shift: number,
        current_week: number,
        current_year: number,
        current_timestmap: number
    ) {
        let style = "col-lg-12 col-md-12";
        let compact = false;

        // special style in case of only one week and showing order list
        if (this.state.show_order_table && this.state.week_offsets.length === 1) {
            style = "col-lg-6 col-md-6";
            compact = true;
        }

        const plant = this.props.data_def.filters.plant;
        const show_setup_code = BusinessLogic.getPlantTagBool(plant, PLANT_TAGS.microplan_show_setup_code, false);
        const show_barcode = BusinessLogic.getPlantTagBool(plant, PLANT_TAGS.show_barcode, false);

        // check if we should render earliest dates
        const table_settings = this.getTableSettings(this.props.line_group_uuid);

        return (
            <div>
                {this.state.show_title && this.renderTitle(line)}

                <div className="row">
                    {this.state.show_line_gantt && (
                        <div className={`${style} position-static`}>
                            <GantchartStandalone
                                history={this.props.history}
                                data_def={{ ...this.props.data_def, show_only_tool_changes: "false" }}
                                source_type={this.props.data && this.props.data.source_type}
                                onOrderChange={this.handleOrderChange}
                            />
                        </div>
                    )}

                    {this.state.show_shift_table && (
                        <div className={style}>
                            <PlanShiftTableProduction
                                line_uuid={line.uuid}
                                realization={this.state.weekly_realization}
                                orders={line_orders}
                                events={line_insights}
                                current_shift={current_shift}
                                week_offsets={this.state.week_offsets}
                                week={current_week}
                                year={current_year}
                            />
                        </div>
                    )}

                    {this.state.show_order_table && (
                        <div className={style}>
                            <OrderTableProduction
                                history={this.props.history}
                                orders={this.filterOrders<rt.ISimulationReportOrderEx>(line_orders)}
                                unscheduled_orders={this.filterOrders<t.IOrderProducedModel>(line_unscheduled_orders)}
                                order_shifts={line_order_shifts}
                                start_timestamp={current_timestmap}
                                compact={compact}
                                earliest_dates={table_settings.show_sap_dates}
                                show_order_shifts={table_settings.show_shifts}
                                show_setup_code={show_setup_code}
                                show_barcode={show_barcode}
                                show_operation_comment={table_settings.show_operation_comment}
                                show_material_comment={table_settings.show_material_comment}
                                show_external_operation_id={table_settings.show_external_operation_id}
                                show_alternative_unit={table_settings.show_alternative_unit}
                                show_material={table_settings.show_material}
                                show_order_id={table_settings.show_order_id}
                                show_operation={table_settings.show_operation}
                                show_expected_start={table_settings.show_expected_start}
                                show_expected_completion={table_settings.show_expected_completion}
                                show_time_to_complete={table_settings.show_time_to_complete}
                                show_custom_field={table_settings.show_custom_field}
                                show_quantity={table_settings.show_quantity}
                                showLinePlanningModal={this.showLinePlanningModal(line)}
                                line={line}
                                materials={this.state.materials}
                                operations={this.state.operations}
                                onOrderChange={this.handleOrderChange}
                                sim_created_at={this.state.simulation ? this.state.simulation.created_at : null}
                                report_uuid={this.state.simulation ? this.state.simulation.uuid : undefined}
                                report={this.state.simulation}
                                scheduling_horizon={this.getSchedulingHorizon()}
                                custom_field_title={this.getCustomFieldTitle()}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderLines(current_shift: number, current_week: number, current_year: number, current_timestmap: number) {
        const lines_components: React.ReactNode[] = [];
        for (const line of this.state.lines) {
            // get orders
            const line_orders = this.state.line_orders.get(line.uuid) || [];
            const line_order_shifts = this.state.line_order_shifts.get(line.uuid) || new Map();
            const line_unscheduled_orders = this.state.line_unscheduled_orders.get(line.uuid) || [];
            // get insights
            let line_insights = this.state.insights.get(line.uuid);
            if (!line_insights) {
                line_insights = new Map();
            }
            // render
            lines_components.push(
                <div key={line.uuid}>
                    {this.renderLine(
                        line,
                        line_orders,
                        line_unscheduled_orders,
                        line_order_shifts,
                        line_insights,
                        current_shift,
                        current_week,
                        current_year,
                        current_timestmap
                    )}
                </div>
            );
        }
        return lines_components;
    }

    renderPrintModal = () => {
        let table_settings = this.getTableSettings();

        if (this.state.lines.length) {
            table_settings = this.getTableSettings(this.props.line_group_uuid);
        }

        return (
            <PrintModal
                export_type={this.state.export_type}
                show={this.state.show_print_modal}
                show_material={table_settings.show_material}
                show_material_comment={table_settings.show_material_comment}
                show_order_id={table_settings.show_order_id}
                show_operation={table_settings.show_operation}
                show_operation_comment={table_settings.show_operation_comment}
                show_expected_start={table_settings.show_expected_start}
                show_expected_completion={table_settings.show_expected_completion}
                show_alternative_unit={table_settings.show_alternative_unit}
                show_time_to_complete={table_settings.show_time_to_complete}
                show_custom_field={table_settings.show_custom_field}
                show_quantity={table_settings.show_quantity}
                show_sap_dates={table_settings.show_sap_dates}
                show_order_shifts={table_settings.show_shifts}
                onPrint={this.handlePrintModalPrint}
                onHide={this.handlePrintModalHide}
            />
        );
    };

    render() {
        let content = <Loader />;

        // wait for data to be loaded from the backend
        let current_week: number | null = null;
        const sim = this.state.simulation;
        const is_loaded =
            this.state.loaded_simulation && this.state.loaded_weekly_realization && this.state.loaded_insights;
        if (is_loaded && sim !== null) {
            // if no orders found, give up
            if (sim.result.orders.length === 0) {
                content = <div>No shift tables data</div>;
            }
            // check the shift number
            if (sim.result && sim.result.next_shift_time) {
                const current_shift = sim.result.next_shift_time.shift_number;
                current_week = sim.result.next_shift_time.week;
                const current_year = sim.result.next_shift_time.year;
                const current_timestmap = dateFromWeekAndShift(current_week, current_year, current_shift).getTime();
                content = (
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            {this.renderLineScheduleModal()}
                            {this.renderLineInsightsModal()}
                            {this.renderLineMaterialsModal()}
                            {this.renderLines(current_shift, current_week!, current_year, current_timestmap)}
                        </div>
                    </div>
                );
            }
        }

        return (
            <div ref={this.nodeRef} id="microplan">
                <ShiftTableToolbar
                    is_loading={!is_loaded}
                    last_refresh_date={sim !== null ? sim.created_at : undefined}
                    available_order_types={this.state.available_order_types}
                    selected_order_types={this.state.selected_order_types}
                    show_order_type_filter={this.isShowOrderTypesFilterEnabled()}
                    onOrderTypesChange={this.handleOrderTypesChange}
                />
                {content}
                {this.renderPrintModal()}
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const line_group_uuid = state.persistent_filters.line_group_uuid;
    return { line_group_uuid };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;
const ConnectedShiftTableStandalone = connect<
    ReturnType<typeof mapState>,
    ReturnType<typeof mapDispatch>,
    OwnProps,
    ReduxState
>(
    mapState,
    mapDispatch
)(ShiftTableStandalone);

export default ConnectedShiftTableStandalone;
